import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { topicsAPI } from '../lib/api';
import { 
  Brain, 
  Cpu, 
  Code, 
  Leaf, 
  Heart, 
  Palette,
  ArrowRight 
} from 'lucide-react';

const topicIcons = {
  'ai-automation': Brain,
  'robotics-manufacturing': Cpu,
  'saas-software': Code,
  'sustainability': Leaf,
  'healthtech': Heart,
  'creative-tech': Palette,
};

const defaultTopics = [
  {
    id: '1',
    name: 'AI & Automation',
    slug: 'ai-automation',
    description: 'Local AI developments, tools, applications, and the future of automation in Portland.',
    article_count: 24,
  },
  {
    id: '2',
    name: 'Robotics & Manufacturing',
    slug: 'robotics-manufacturing',
    description: 'Portland hardware innovation, supply chain, and the future of manufacturing.',
    article_count: 18,
  },
  {
    id: '3',
    name: 'SaaS & Software',
    slug: 'saas-software',
    description: 'Portland\'s software companies, product development, and engineering culture.',
    article_count: 32,
  },
  {
    id: '4',
    name: 'Sustainability Tech',
    slug: 'sustainability',
    description: 'Cleantech, green technology, and environmental innovation from the Pacific Northwest.',
    article_count: 15,
  },
  {
    id: '5',
    name: 'Health Tech & Biotech',
    slug: 'healthtech',
    description: 'Medical devices, digital health, life sciences, and healthcare innovation.',
    article_count: 12,
  },
  {
    id: '6',
    name: 'Creative Technology',
    slug: 'creative-tech',
    description: 'Film tech, gaming, VR/AR, digital media, and the intersection of art and technology.',
    article_count: 20,
  },
];

export const TopicsPage = () => {
  const [topics, setTopics] = useState(defaultTopics);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await topicsAPI.getAll();
        if (res.data.length > 0) {
          setTopics(res.data);
        }
      } catch (err) {
        console.error('Error fetching topics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopics();
  }, []);

  return (
    <div data-testid="topics-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Knowledge</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Topics & Insights</h1>
          <p className="text-muted-foreground max-w-2xl">
            Deep dives into Portland's technology sectors. Explore trends, 
            innovations, and insights across the Portland tech ecosystem.
          </p>
        </div>
      </section>

      {/* Topics Grid */}
      <section>
        <div className="container-wide py-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((topic, index) => {
              const IconComponent = topicIcons[topic.slug] || Brain;
              return (
                <Link
                  key={topic.id}
                  to={`/topics/${topic.slug}`}
                  className="group animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`topic-card-${topic.slug}`}
                >
                  <Card className="card-hover invert-hover h-full">
                    <CardContent className="p-8">
                      <div className="w-12 h-12 rounded-sm bg-muted group-hover:bg-primary-foreground/20 flex items-center justify-center mb-6 transition-colors">
                        <IconComponent className="w-6 h-6 text-muted-foreground group-hover:text-primary-foreground" />
                      </div>
                      <h3 className="font-semibold text-xl mb-3">
                        {topic.name}
                      </h3>
                      <p className="text-muted-foreground group-hover:text-primary-foreground/70 text-sm mb-6">
                        {topic.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-muted-foreground group-hover:text-primary-foreground/60">
                          {topic.article_count} articles
                        </span>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Insights - could be expanded */}
      <section className="border-t border-border bg-muted/30">
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-label text-muted-foreground mb-4 block">Coming Soon</span>
            <h2 className="text-heading text-2xl mb-4">Expert Insights & Analysis</h2>
            <p className="text-muted-foreground">
              In-depth articles, interviews with local tech leaders, market analysis, 
              and exclusive content about Portland's technology ecosystem.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
